import { CheckIcon } from '@phosphor-icons/react';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useApi } from '@/contexts/ApiContext';
import { useGlobalState } from '@/contexts/GlobalStateContext';
import { useToast } from '@/contexts/ToastContext';

// Types
interface PurchaseOrderNoteFormData {
  transportationCost: number;
  packagingCost: number;
  miscellaneousCost: number;
  loadingPoint: string;
  deliveryPoint: string;
  deliveryNote: string;
}

interface PurchaseOrderNoteCreateSheetProps {
  purchaseOrder: {
    _id: string;
    purchaseOrderNumber: string;
    supplier?: {
      businessName: string;
      contactName: string;
    };
    items?: Array<{
      inventory: { name: string; measurementUnit?: string };
      quantity: number;
      unitPrice: number;
    }>;
    totalAmount?: number;
    expectedDeliveryDate?: string;
  };
  onNoteCreated?: () => Promise<void>;
}

const PurchaseOrderNoteCreateSheet: React.FC<
  PurchaseOrderNoteCreateSheetProps
> = ({ purchaseOrder, onNoteCreated }) => {
  const api = useApi();
  const { hideSheet } = useGlobalState();
  const { showSuccess, showError } = useToast();

  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const totalSteps = 2;

  const { control, handleSubmit, watch, trigger, reset } =
    useForm<PurchaseOrderNoteFormData>({
      defaultValues: {
        transportationCost: 0,
        packagingCost: 0,
        miscellaneousCost: 0,
        loadingPoint: '',
        deliveryPoint: '',
        deliveryNote: '',
      },
      mode: 'onChange',
    });

  const watchedValues = watch();

  // Navigation functions
  const handleNextStep = async () => {
    let fieldsToValidate: (keyof PurchaseOrderNoteFormData)[] = [];

    if (currentStep === 1) {
      fieldsToValidate = [
        'transportationCost',
        'packagingCost',
        'miscellaneousCost',
        'loadingPoint',
        'deliveryPoint',
        'deliveryNote',
      ];
    }

    const isValid = await trigger(fieldsToValidate);
    if (isValid && currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCancel = () => {
    reset();
    setCurrentStep(1);
    hideSheet('create-purchase-order-note');
  };

  const handleSubmitForm = async () => {
    if (currentStep < totalSteps) {
      return handleNextStep();
    }

    // Final submission
    setIsLoading(true);
    try {
      const formData = watch();
      const noteData = {
        transportationCost: formData.transportationCost,
        packagingCost: formData.packagingCost,
        miscCost: formData.miscellaneousCost,
        loadingPoint: formData.loadingPoint,
        deliveryPoint: formData.deliveryPoint,
        deliveryNote: formData.deliveryNote,
      };

      const response = await api.post(
        `/purchase-order/${purchaseOrder._id}/receipt`,
        { note: noteData }
      );

      if (response.success) {
        showSuccess(
          response.message || 'Purchase order note created successfully'
        );
        reset();
        setCurrentStep(1);
        hideSheet('create-purchase-order-note');

        if (onNoteCreated) {
          await onNoteCreated();
        }
      } else {
        showError(
          response.error?.message || 'Failed to create purchase order note'
        );
      }
    } catch (error) {
      showError('An error occurred while creating the purchase order note');
    } finally {
      setIsLoading(false);
    }
  };

  // Expose handlers to global window object for GlobalSheetRenderer
  useEffect(() => {
    const handlers = {
      handleNextStep,
      handlePrevStep,
      handleSubmit: handleSubmitForm,
      handleCancel,
      currentStep,
      totalSteps,
      isLoading,
    };

    // Store handlers globally
    if (!(window as any).purchaseOrderNoteCreateHandlers) {
      (window as any).purchaseOrderNoteCreateHandlers = {};
    }
    (window as any).purchaseOrderNoteCreateHandlers = handlers;

    // Cleanup on unmount
    return () => {
      if ((window as any).purchaseOrderNoteCreateHandlers) {
        delete (window as any).purchaseOrderNoteCreateHandlers;
      }
    };
  }, [currentStep, totalSteps, isLoading]);

  const onSubmit = async () => {
    await handleSubmitForm();
  };

  const calculateTotalCosts = () => {
    return (
      watchedValues.transportationCost +
      watchedValues.packagingCost +
      watchedValues.miscellaneousCost
    );
  };

  const calculateGrandTotal = () => {
    return (purchaseOrder.totalAmount || 0) + calculateTotalCosts();
  };

  const formatCurrency = (amount: number) => {
    return `₦${amount.toLocaleString()}`;
  };

  return (
    <div className='space-y-6'>
      {/* Step indicator */}
      <div className='flex justify-center mb-6'>
        <div className='flex items-center space-x-4'>
          <div className='flex items-center'>
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                currentStep >= 1
                  ? 'bg-brand-600 text-white'
                  : 'bg-gray-30 text-gray-70'
              }`}
            >
              {currentStep > 1 ? <CheckIcon className='w-4 h-4' /> : '1'}
            </div>
            <span className='ml-2 text-sm font-medium text-gray-90'>
              Order Note Details
            </span>
          </div>
          <div className='w-16 h-0.5 bg-gray-30' />
          <div className='flex items-center'>
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                currentStep >= 2
                  ? 'bg-brand-600 text-white'
                  : 'bg-gray-30 text-gray-70'
              }`}
            >
              {currentStep > 2 ? <CheckIcon className='w-4 h-4' /> : '2'}
            </div>
            <span className='ml-2 text-sm font-medium text-gray-90'>
              Summary
            </span>
          </div>
        </div>
      </div>

      {/* Purchase Order Info */}
      <div className='bg-gray-5 p-4 rounded-lg'>
        <h4 className='text-sm font-medium text-gray-90 mb-3'>
          Purchase Order Information
        </h4>
        <div className='space-y-2 text-sm'>
          <div className='flex justify-between'>
            <span className='text-gray-60'>PO Number:</span>
            <span className='font-mono font-semibold'>
              {purchaseOrder.purchaseOrderNumber}
            </span>
          </div>
          {purchaseOrder.supplier && (
            <div className='flex justify-between'>
              <span className='text-gray-60'>Supplier:</span>
              <span className='font-medium'>
                {purchaseOrder.supplier.businessName}
              </span>
            </div>
          )}
          <div className='flex justify-between'>
            <span className='text-gray-60'>Items:</span>
            <span>{purchaseOrder.items?.length || 0} items</span>
          </div>
          {purchaseOrder.totalAmount && (
            <div className='flex justify-between font-semibold border-t pt-2'>
              <span>Order Amount:</span>
              <span>{formatCurrency(purchaseOrder.totalAmount)}</span>
            </div>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
        {/* Step 1: Order Note Details */}
        {currentStep === 1 && (
          <div className='space-y-4'>
            {/* Cost Fields */}
            <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
              <div className='space-y-2'>
                <Label htmlFor='transportationCost'>
                  Transportation Cost (₦) *
                </Label>
                <Controller
                  name='transportationCost'
                  control={control}
                  rules={{
                    required: 'Transportation cost is required',
                    min: { value: 0, message: 'Cost cannot be negative' },
                  }}
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      type='number'
                      step='0.01'
                      min='0'
                      placeholder='0.00'
                      error={fieldState?.error?.message}
                      onChange={e => field.onChange(Number(e.target.value))}
                    />
                  )}
                />
              </div>

              <div className='space-y-2'>
                <Label htmlFor='packagingCost'>Packaging Cost (₦) *</Label>
                <Controller
                  name='packagingCost'
                  control={control}
                  rules={{
                    required: 'Packaging cost is required',
                    min: { value: 0, message: 'Cost cannot be negative' },
                  }}
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      type='number'
                      step='0.01'
                      min='0'
                      placeholder='0.00'
                      error={fieldState?.error?.message}
                      onChange={e => field.onChange(Number(e.target.value))}
                    />
                  )}
                />
              </div>

              <div className='space-y-2'>
                <Label htmlFor='miscellaneousCost'>
                  Miscellaneous Cost (₦) *
                </Label>
                <Controller
                  name='miscellaneousCost'
                  control={control}
                  rules={{
                    required: 'Miscellaneous cost is required',
                    min: { value: 0, message: 'Cost cannot be negative' },
                  }}
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      type='number'
                      step='0.01'
                      min='0'
                      placeholder='0.00'
                      error={fieldState?.error?.message}
                      onChange={e => field.onChange(Number(e.target.value))}
                    />
                  )}
                />
              </div>
            </div>

            {/* Additional Costs Summary */}
            {calculateTotalCosts() > 0 && (
              <div className='bg-brand-5 p-3 rounded-lg'>
                <div className='flex justify-between text-sm'>
                  <span className='text-brand-70'>Total Additional Costs:</span>
                  <span className='font-semibold text-brand-90'>
                    {formatCurrency(calculateTotalCosts())}
                  </span>
                </div>
              </div>
            )}

            {/* Location Fields */}
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label htmlFor='loadingPoint'>Loading Point *</Label>
                <Controller
                  name='loadingPoint'
                  control={control}
                  rules={{ required: 'Loading point is required' }}
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      placeholder='Enter loading point address'
                      error={fieldState?.error?.message}
                    />
                  )}
                />
              </div>

              <div className='space-y-2'>
                <Label htmlFor='deliveryPoint'>Delivery Point *</Label>
                <Controller
                  name='deliveryPoint'
                  control={control}
                  rules={{ required: 'Delivery point is required' }}
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      placeholder='Enter delivery point address'
                      error={fieldState?.error?.message}
                    />
                  )}
                />
              </div>
            </div>

            {/* Delivery Note */}
            <div className='space-y-2'>
              <Label htmlFor='deliveryNote'>Delivery Note *</Label>
              <Controller
                name='deliveryNote'
                control={control}
                rules={{
                  required: 'Delivery note is required',
                  minLength: {
                    value: 10,
                    message: 'Delivery note must be at least 10 characters',
                  },
                }}
                render={({ field, fieldState }) => (
                  <>
                    <Textarea
                      {...field}
                      placeholder='Enter delivery instructions, special requirements, or other relevant notes'
                      rows={4}
                      maxLength={300}
                    />
                    {fieldState?.error && (
                      <p className='text-sm text-destructive-60'>
                        {fieldState.error.message}
                      </p>
                    )}
                  </>
                )}
              />
              <p className='text-xs text-gray-50'>
                {watchedValues.deliveryNote?.length || 0}/300 characters
              </p>
            </div>
          </div>
        )}

        {/* Step 2: Summary */}
        {currentStep === 2 && (
          <div className='space-y-4'>
            {/* Order Items Summary */}
            {purchaseOrder.items && purchaseOrder.items.length > 0 && (
              <div className='bg-gray-5 p-4 rounded-lg'>
                <h4 className='text-sm font-medium text-gray-90 mb-3'>
                  Order Items ({purchaseOrder.items.length})
                </h4>
                <div className='overflow-x-auto'>
                  <table className='w-full text-sm'>
                    <thead>
                      <tr className='border-b border-gray-20'>
                        <th className='text-left py-2'>Item Name</th>
                        <th className='text-right py-2'>Quantity</th>
                        <th className='text-right py-2'>Unit Price</th>
                        <th className='text-right py-2'>Sub Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {purchaseOrder.items.map((item, index) => (
                        <tr key={index} className='border-b border-gray-10'>
                          <td className='py-2 font-medium'>
                            {item.inventory.name}
                          </td>
                          <td className='text-right py-2'>
                            {item.quantity.toLocaleString()}
                            {item.inventory.measurementUnit &&
                              ` ${item.inventory.measurementUnit}`}
                          </td>
                          <td className='text-right py-2'>
                            {formatCurrency(item.unitPrice)}
                          </td>
                          <td className='text-right py-2 font-semibold'>
                            {formatCurrency(item.quantity * item.unitPrice)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Cost Summary */}
            <div className='bg-gray-5 p-4 rounded-lg'>
              <h4 className='text-sm font-medium text-gray-90 mb-3'>
                Cost Breakdown
              </h4>
              <div className='space-y-2 text-sm'>
                <div className='flex justify-between'>
                  <span>Purchase Order Total:</span>
                  <span>{formatCurrency(purchaseOrder.totalAmount || 0)}</span>
                </div>
                <div className='flex justify-between'>
                  <span>Transportation Cost:</span>
                  <span>
                    {formatCurrency(watchedValues.transportationCost || 0)}
                  </span>
                </div>
                <div className='flex justify-between'>
                  <span>Packaging Cost:</span>
                  <span>
                    {formatCurrency(watchedValues.packagingCost || 0)}
                  </span>
                </div>
                <div className='flex justify-between'>
                  <span>Miscellaneous Cost:</span>
                  <span>
                    {formatCurrency(watchedValues.miscellaneousCost || 0)}
                  </span>
                </div>
                <div className='flex justify-between font-semibold text-base border-t pt-2'>
                  <span>Grand Total:</span>
                  <span>{formatCurrency(calculateGrandTotal())}</span>
                </div>
              </div>
            </div>

            {/* Delivery Information */}
            <div className='bg-gray-5 p-4 rounded-lg'>
              <h4 className='text-sm font-medium text-gray-90 mb-3'>
                Delivery Information
              </h4>
              <div className='space-y-2 text-sm'>
                <div>
                  <span className='text-gray-60'>Loading Point:</span>
                  <p className='font-medium'>{watchedValues.loadingPoint}</p>
                </div>
                <div>
                  <span className='text-gray-60'>Delivery Point:</span>
                  <p className='font-medium'>{watchedValues.deliveryPoint}</p>
                </div>
                <div>
                  <span className='text-gray-60'>Delivery Note:</span>
                  <p className='font-medium text-gray-90 bg-white p-2 rounded border'>
                    {watchedValues.deliveryNote}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default PurchaseOrderNoteCreateSheet;
